import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { useQueryClient } from '@tanstack/react-query';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Search, X } from 'lucide-react';
import { useInvoices, useInvoiceStats } from '@/hooks/useInvoices';
import { useQuickViewNavigation } from '@/hooks/useQuickViewNavigation';
import { InvoicesTableView } from '@/components/invoices/InvoicesTableView';
import { InvoiceSummaryCards } from '@/components/invoices/InvoiceSummaryCards';
import { SelectJobForInvoiceDialog, InvoiceableJob } from '@/components/invoices/SelectJobForInvoiceDialog';
import { InvoiceFromJobDialog } from '@/components/invoices/InvoiceFromJobDialog';
import { InvoiceQuickViewSheet } from '@/components/invoices/InvoiceQuickViewSheet';
import { InvoicePreview } from '@/components/invoices/InvoicePreview';
import { documentPageStyle } from '@/components/documents/print';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { fetchJobPayments, jobPaymentsQueryKey } from '@/hooks/useJobPayments';
import { AdminOnly } from '@/components/AdminOnly';
import { InvoiceWithRelations, InvoiceStatus } from '@/types/invoice';

export default function Invoices() {
  const { id: routeInvoiceId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  // Invoices are always raised against a job: pick the job first, then build the invoice from it
  const [invoiceJob, setInvoiceJob] = useState<InvoiceableJob | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(routeInvoiceId ?? null);

  // /invoices/:id opens the quick view directly (shareable deep link)
  useEffect(() => {
    if (routeInvoiceId) setSelectedInvoiceId(routeInvoiceId);
  }, [routeInvoiceId]);

  const closeQuickView = () => {
    setSelectedInvoiceId(null);
    if (routeInvoiceId) navigate('/invoices', { replace: true });
  };
  const [printInvoice, setPrintInvoice] = useState<InvoiceWithRelations | null>(null);

  const printRef = useRef<HTMLDivElement>(null);
  const { locale } = useCompanyLocale();
  const queryClient = useQueryClient();

  const { data: invoices = [], isLoading: loadingInvoices } = useInvoices({
    status: statusFilter,
    search: searchQuery || undefined,
  });

  const { data: stats, isLoading: loadingStats } = useInvoiceStats();

  // Quick view navigation
  const { hasPrevious, hasNext, goToPrevious, goToNext } = useQuickViewNavigation({
    items: invoices,
    getItemId: (invoice) => invoice.id,
    selectedId: selectedInvoiceId,
    onSelect: setSelectedInvoiceId,
  });

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: printInvoice?.invoice_number || 'Invoice',
    pageStyle: documentPageStyle(locale.paper),
  });

  const handleViewInvoice = (invoice: InvoiceWithRelations) => {
    setSelectedInvoiceId(invoice.id);
  };

  const handlePrintInvoice = async (invoice: InvoiceWithRelations) => {
    // Load the job's payments first so part payments are on the printout.
    await queryClient
      .ensureQueryData({ queryKey: jobPaymentsQueryKey(invoice.job_id ?? null), queryFn: () => fetchJobPayments(invoice.job_id ?? null) })
      .catch(() => undefined);
    setPrintInvoice(invoice);
    // Small delay to ensure component is mounted
    setTimeout(() => {
      handlePrint();
    }, 100);
  };

  const getStatusCount = (status: InvoiceStatus | 'all') => {
    if (status === 'all') return stats?.invoiceCount || 0;
    switch (status) {
      case 'draft': return stats?.draftCount || 0;
      case 'sent': return stats?.sentCount || 0;
      case 'paid': return stats?.paidCount || 0;
      case 'overdue': return stats?.overdueCount || 0;
      case 'refunded': return stats?.refundedCount || 0;
      default: return 0;
    }
  };

  const statusTabs: { value: InvoiceStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'paid', label: 'Paid' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'refunded', label: 'Refunded' },
  ];

  return (
    <Layout>
      <div className="space-y-[22px]">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Invoices</h1>
            <p className="mt-1 text-[13px] text-muted-foreground">Manage your invoices and billing</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            New Invoice
          </Button>
        </div>

        {/* Summary Cards */}
        <AdminOnly>
          <InvoiceSummaryCards
            stats={stats || {
              totalInvoiced: 0,
              totalPaid: 0,
              totalOutstanding: 0,
              totalOverdue: 0,
              invoiceCount: 0,
              draftCount: 0,
              sentCount: 0,
              paidCount: 0,
              overdueCount: 0,
              refundedCount: 0,
            }}
            isLoading={loadingStats}
          />
        </AdminOnly>

        {/* Table card: underline status tabs + search in the header row */}
        <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as InvoiceStatus | 'all')}>
          <Card className="overflow-hidden">
            <div className="flex flex-col gap-2 border-b px-5 pt-2 sm:flex-row sm:items-end sm:justify-between">
              <TabsList className="-mb-px h-auto justify-start gap-4 rounded-none bg-transparent p-0">
                {statusTabs.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 text-[13px] font-medium text-muted-foreground shadow-none transition-colors hover:text-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                    {tab.label}
                    {stats && (
                      <span className="ml-1.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary tabular-nums">
                        {getStatusCount(tab.value)}
                      </span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
              <div className="pb-2.5 sm:w-72">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search invoices..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-9 pl-10"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <TabsContent value={statusFilter} className="mt-0">
              <InvoicesTableView
                invoices={invoices}
                isLoading={loadingInvoices}
                onViewInvoice={handleViewInvoice}
                onPrintInvoice={handlePrintInvoice}
              />
            </TabsContent>
          </Card>
        </Tabs>
      </div>

      {/* New invoice: choose a job, then generate the invoice from it */}
      <SelectJobForInvoiceDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSelect={(job) => {
          setShowCreateDialog(false);
          setInvoiceJob(job);
        }}
        onOpenExisting={(invoiceId) => {
          setShowCreateDialog(false);
          navigate(`/invoices/${invoiceId}`);
        }}
      />
      {invoiceJob && (
        <InvoiceFromJobDialog
          open={!!invoiceJob}
          onOpenChange={(open) => !open && setInvoiceJob(null)}
          job={invoiceJob}
        />
      )}

      {/* Invoice Quick View Sheet */}
      <InvoiceQuickViewSheet
        invoiceId={selectedInvoiceId}
        open={!!selectedInvoiceId}
        onOpenChange={(open) => !open && closeQuickView()}
        onPrevious={hasPrevious ? goToPrevious : undefined}
        onNext={hasNext ? goToNext : undefined}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
      />

      {/* Hidden Print Component */}
      {printInvoice && (
        <div className="hidden print:block">
          <InvoicePreview ref={printRef} invoice={printInvoice} />
        </div>
      )}
    </Layout>
  );
}
